// src/pages/problem-bank/ExternalSubmissions.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import {
  ArrowLeft,
  Search,
  Eye,
  CheckCircle,
  Building2,
  Mail,
  Phone,
  FileText,
  RefreshCw,
  User,
  Briefcase,
  DollarSign,
  TrendingUp,
  Target,
  MapPin,
  Tag,
  ChevronDown,
  ChevronUp,
  Loader2,
  Info,
  Shield,
  UserCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import React from 'react';

type SubmissionType = 'company' | 'public';

interface BaseSubmission {
  id: string;
  type: SubmissionType;
  status: string;
  submittedAt: string;
  reviewedAt?: string;
  reviewNotes?: string;
  convertedToProblemId?: string;
  assignedTo?: string;
  assignedToName?: string;
  assignedAt?: string;
}

interface CompanySubmission extends BaseSubmission {
  type: 'company';
  companyName: string;
  companyRegistration?: string;
  industrySector?: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  problemTitle: string;
  problemDescription: string;
  category: string[];
  urgency: string;
  estimatedImpact?: string;
  totalAnnualCost?: number;
  quantification?: any;
  rootCause?: string;
  pilotReadiness?: any;
  technicalInfo?: any;
  documents?: string[];
}

interface PublicSubmission extends BaseSubmission {
  type: 'public';
  fullName: string;
  email: string;
  phone?: string;
  organization?: string;
  problemTitle: string;
  problemDescription: string;
  industrySector?: string;
  category?: string[];
  urgency: string;
  location?: string;
  estimatedImpact?: string;
  documents?: string[];
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  assigned: 'bg-purple-100 text-purple-700 border-purple-200',
  under_review: 'bg-blue-100 text-blue-700 border-blue-200',
  converted: 'bg-gray-100 text-gray-700 border-gray-200',
  rejected: 'bg-red-100 text-red-700 border-red-200'
};

const URGENCY_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200'
};

export default function ExternalSubmissions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const isProblemBankAnalyst = user?.analyst_type === 'problem_bank';
  const isSeniorAnalyst = user?.role === 'senior_analyst';
  const isBusinessAnalyst = user?.analyst_type === 'business';
  
  const [submissions, setSubmissions] = useState<(CompanySubmission | PublicSubmission)[]>([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState<(CompanySubmission | PublicSubmission)[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [assignedFilter, setAssignedFilter] = useState<string>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<CompanySubmission | PublicSubmission | null>(null);
  const [selectedAnalystId, setSelectedAnalystId] = useState<string>('');
  const [availableAnalysts, setAvailableAnalysts] = useState<any[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  
  const hasLoaded = useRef(false);

  if (isBusinessAnalyst) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground text-center max-w-md">
          External Submissions are only accessible to Problem Bank Analysts and Senior Analysts.
        </p>
        <Button className="mt-6" onClick={() => navigate('/analyst-dashboard')}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  const loadAvailableAnalysts = useCallback(async () => {
    if (!isSeniorAnalyst) return [];
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, analyst_type')
        .eq('analyst_type', 'problem_bank')
        .in('role', ['analyst', 'senior_analyst']);

      if (error) throw error;
      setAvailableAnalysts(data || []);
      return data || [];
    } catch (error) {
      console.error('Error loading problem bank analysts:', error);
      return [];
    }
  }, [isSeniorAnalyst]);

  const loadSubmissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('external_submissions')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      const mappedSubmissions: (CompanySubmission | PublicSubmission)[] = (data || []).map(sub => {
        let quantification = null;
        if (sub.quantification) {
          try {
            quantification = typeof sub.quantification === 'string' 
              ? JSON.parse(sub.quantification) 
              : sub.quantification;
          } catch (e) {
            console.error('Error parsing quantification:', e);
          }
        }
        
        let rootCause = null;
        if (sub.root_cause) {
          try {
            rootCause = typeof sub.root_cause === 'string' 
              ? JSON.parse(sub.root_cause) 
              : sub.root_cause;
          } catch (e) {
            console.error('Error parsing root_cause:', e);
            rootCause = sub.root_cause;
          }
        }
        
        const isCompany = sub.company_name && sub.company_name !== 'Individual Submission';
        
        if (isCompany) {
          return {
            id: sub.id,
            type: 'company',
            status: sub.status,
            submittedAt: sub.submitted_at,
            reviewedAt: sub.reviewed_at,
            reviewNotes: sub.review_notes,
            convertedToProblemId: sub.converted_to_problem_id,
            assignedTo: sub.assigned_to,
            assignedToName: sub.assigned_to_name,
            assignedAt: sub.assigned_at,
            companyName: sub.company_name,
            companyRegistration: sub.company_registration,
            industrySector: sub.industry_sector,
            contactName: sub.contact_name,
            contactTitle: sub.contact_title || 'Contact',
            contactEmail: sub.contact_email,
            contactPhone: sub.contact_phone,
            problemTitle: sub.problem_title,
            problemDescription: sub.problem_description,
            category: sub.category || ['other'],
            urgency: sub.urgency,
            estimatedImpact: sub.estimated_impact,
            totalAnnualCost: sub.total_annual_cost || quantification?.totalLossPerYear || 0,
            quantification: quantification,
            rootCause: rootCause?.details || rootCause?.rootCauseDetails || (typeof sub.root_cause === 'string' ? sub.root_cause : null),
            pilotReadiness: sub.pilot_readiness,
            technicalInfo: sub.technical_info,
            documents: sub.documents || []
          } as CompanySubmission;
        } else {
          return {
            id: sub.id,
            type: 'public',
            status: sub.status,
            submittedAt: sub.submitted_at,
            reviewedAt: sub.reviewed_at,
            reviewNotes: sub.review_notes,
            convertedToProblemId: sub.converted_to_problem_id,
            assignedTo: sub.assigned_to,
            assignedToName: sub.assigned_to_name,
            assignedAt: sub.assigned_at,
            fullName: sub.contact_name,
            email: sub.contact_email,
            phone: sub.contact_phone,
            problemTitle: sub.problem_title,
            problemDescription: sub.problem_description,
            industrySector: sub.industry_sector,
            category: sub.category || ['other'],
            urgency: sub.urgency,
            location: sub.location,
            estimatedImpact: sub.estimated_impact,
            documents: sub.documents || []
          } as PublicSubmission;
        }
      });

      setSubmissions(mappedSubmissions);
      setFilteredSubmissions(mappedSubmissions);
      
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load submissions',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadSubmissions();
      if (isSeniorAnalyst) {
        loadAvailableAnalysts();
      }
    }
  }, [loadSubmissions, loadAvailableAnalysts, isSeniorAnalyst]);

  useEffect(() => {
    filterSubmissions();
  }, [submissions, searchQuery, statusFilter, typeFilter, assignedFilter]);

  const filterSubmissions = () => {
    let filtered = [...submissions];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => {
        if (s.type === 'company') {
          const company = s as CompanySubmission;
          return company.companyName.toLowerCase().includes(query) ||
                 company.contactName.toLowerCase().includes(query) ||
                 company.contactEmail.toLowerCase().includes(query) ||
                 company.problemTitle.toLowerCase().includes(query);
        } else {
          const pub = s as PublicSubmission;
          return pub.fullName.toLowerCase().includes(query) ||
                 pub.email.toLowerCase().includes(query) ||
                 pub.problemTitle.toLowerCase().includes(query);
        }
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(s => s.type === typeFilter);
    }

    if (assignedFilter === 'assigned_to_me' && user?.id) {
      filtered = filtered.filter(s => s.assignedTo === user.id);
    } else if (assignedFilter === 'unassigned') {
      filtered = filtered.filter(s => !s.assignedTo);
    } else if (assignedFilter === 'assigned') {
      filtered = filtered.filter(s => s.assignedTo);
    }

    setFilteredSubmissions(filtered);
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleAssignClick = (submission: CompanySubmission | PublicSubmission) => {
    setSelectedSubmission(submission);
    setSelectedAnalystId('');
    setAssignmentDialogOpen(true);
  };

  const handleAssignSubmission = async () => {
    if (!selectedSubmission || !selectedAnalystId) {
      toast({
        title: 'Error',
        description: 'Please select an analyst to assign this submission to',
        variant: 'destructive'
      });
      return;
    }

    setIsAssigning(true);
    try {
      const analyst = availableAnalysts.find(a => a.id === selectedAnalystId);
      
      const { error } = await supabase
        .from('external_submissions')
        .update({
          assigned_to: selectedAnalystId,
          assigned_to_name: analyst?.name,
          assigned_at: new Date().toISOString(),
          status: 'under_review',
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedSubmission.id);

      if (error) throw error;

      toast({
        title: 'Submission Assigned',
        description: `Submission has been assigned to ${analyst?.name} and moved to Under Review`,
      });

      setAssignmentDialogOpen(false);
      setSelectedSubmission(null);
      setSelectedAnalystId('');
      loadSubmissions();
      
    } catch (error) {
      console.error('Error assigning submission:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign submission',
        variant: 'destructive'
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleReviewClick = (submission: CompanySubmission | PublicSubmission) => {
    navigate(`/problem-bank/submissions/review/${submission.id}`, {
      state: { submission }
    });
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    await loadSubmissions();
    if (isSeniorAnalyst) {
      await loadAvailableAnalysts();
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const formatCurrency = (value: number) => {
    if (!value) return 'KES 0';
    if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
    return `KES ${value.toLocaleString()}`;
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const pendingCount = submissions.filter(s => s.status === 'pending').length;
  const underReviewCount = submissions.filter(s => s.status === 'under_review').length;
  const convertedCount = submissions.filter(s => s.status === 'converted').length;
  const rejectedCount = submissions.filter(s => s.status === 'rejected').length;
  const assignedCount = submissions.filter(s => s.assignedTo && s.status !== 'converted' && s.status !== 'rejected').length;
  const companyCount = submissions.filter(s => s.type === 'company').length;
  const publicCount = submissions.filter(s => s.type === 'public').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/problem-bank')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">External Submissions</h1>
          <p className="text-muted-foreground mt-1">
            {isSeniorAnalyst 
              ? 'Review and assign submissions to Problem Bank Analysts'
              : 'Review and convert problem submissions from companies and individuals'}
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Total</CardTitle></CardHeader><CardContent><div className="text-xl font-bold">{submissions.length}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Pending</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-yellow-600">{pendingCount}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Under Review</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-blue-600">{underReviewCount}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Converted</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-gray-600">{convertedCount}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Rejected</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-red-600">{rejectedCount}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Assigned</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-purple-600">{assignedCount}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Companies/Public</CardTitle></CardHeader><CardContent><div className="text-sm font-bold"><span className="text-purple-600">{companyCount}</span> / <span className="text-cyan-600">{publicCount}</span></div></CardContent></Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search by company, contact, or problem title..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="pl-9" 
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="company">Company</SelectItem>
            <SelectItem value="public">Public</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={assignedFilter} onValueChange={setAssignedFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by assignment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Submissions</SelectItem>
            <SelectItem value="assigned_to_me">Assigned to Me</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            <SelectItem value="assigned">All Assigned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Submissions Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Submitter</TableHead>
              <TableHead className="min-w-[200px]">Problem</TableHead>
              <TableHead>Assigned To</TableHead>
              <TableHead>Impact</TableHead>
              <TableHead>Urgency</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right min-w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-20" /></TableCell>
                </TableRow>
              ))
            ) : filteredSubmissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p className="text-muted-foreground">No submissions found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredSubmissions.map((submission) => (
                <React.Fragment key={submission.id}>
                  <TableRow className="cursor-pointer hover:bg-muted/50">
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7" 
                        onClick={() => toggleExpand(submission.id)}
                      >
                        {expandedRows.has(submission.id) ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {submission.type === 'company' ? (
                        <Badge className="bg-purple-100 text-purple-700 text-xs">
                          <Building2 className="h-3 w-3 mr-1" />
                          Company
                        </Badge>
                      ) : (
                        <Badge className="bg-cyan-100 text-cyan-700 text-xs">
                          <User className="h-3 w-3 mr-1" />
                          Public
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{getTimeAgo(submission.submittedAt)}</TableCell>
                    <TableCell>
                      {submission.type === 'company' ? (
                        <div className="max-w-[150px]">
                          <div className="text-sm font-medium truncate">{(submission as CompanySubmission).companyName}</div>
                          <div className="text-xs text-muted-foreground truncate">{(submission as CompanySubmission).contactName}</div>
                        </div>
                      ) : (
                        <div className="max-w-[150px]">
                          <div className="text-sm font-medium truncate">{(submission as PublicSubmission).fullName}</div>
                          <div className="text-xs text-muted-foreground truncate">{(submission as PublicSubmission).email}</div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[200px]">
                        <div className="text-sm font-medium truncate">{submission.problemTitle}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {submission.problemDescription.substring(0, 60)}...
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {submission.assignedToName ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs bg-purple-100 text-purple-700">
                              {getInitials(submission.assignedToName)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs font-medium">{submission.assignedToName}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {submission.estimatedImpact ? (
                        <span className="text-xs text-red-600 font-medium whitespace-nowrap">{submission.estimatedImpact}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${URGENCY_COLORS[submission.urgency]} text-xs`}>
                        {submission.urgency}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${STATUS_COLORS[submission.status]} text-xs whitespace-nowrap`}>
                        {submission.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Assign button - only for Senior Analysts */}
                        {isSeniorAnalyst && submission.status === 'pending' && !submission.assignedTo && (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleAssignClick(submission)}
                            className="bg-purple-600 hover:bg-purple-700 h-7 text-xs px-2"
                          >
                            <UserCheck className="h-3 w-3 mr-1" />
                            Assign
                          </Button>
                        )}
                        
                        {/* Reassign button - only for Senior Analysts */}
                        {isSeniorAnalyst && submission.status === 'under_review' && submission.assignedTo && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleAssignClick(submission)}
                            className="h-7 text-xs px-2"
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Reassign
                          </Button>
                        )}
                        
                        {/* REVIEW button - for both Senior Analysts and Problem Bank Analysts */}
                        {(isSeniorAnalyst || isProblemBankAnalyst) && (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleReviewClick(submission)}
                            className="bg-green-600 hover:bg-green-700 h-7 text-xs px-2"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Review
                          </Button>
                        )}
                        
                        {/* Details button - keep for expanding row */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => toggleExpand(submission.id)}
                          className="h-7 text-xs px-2"
                        >
                          {expandedRows.has(submission.id) ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                          Details
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row - Full Details */}
                  {expandedRows.has(submission.id) && (
                    <TableRow className="bg-muted/20">
                      <TableCell colSpan={10} className="p-4">
                        <div className="space-y-4">
                          <h4 className="font-medium text-sm">Full Problem Description</h4>
                          <p className="text-sm text-muted-foreground whitespace-pre-line">
                            {submission.problemDescription}
                          </p>
                          
                          {submission.type === 'company' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                  <Briefcase className="h-4 w-4" />
                                  Company Details
                                </h4>
                                <div className="space-y-1 text-sm">
                                  <p><span className="text-muted-foreground">Industry:</span> {(submission as CompanySubmission).industrySector || 'Not specified'}</p>
                                  <p><span className="text-muted-foreground">Categories:</span> {(submission as CompanySubmission).category?.join(', ') || 'Not specified'}</p>
                                  <p><span className="text-muted-foreground">Contact:</span> {(submission as CompanySubmission).contactTitle}</p>
                                  <p><span className="text-muted-foreground">Phone:</span> {(submission as CompanySubmission).contactPhone}</p>
                                </div>
                              </div>
                              <div>
                                <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                  <DollarSign className="h-4 w-4" />
                                  Financial Impact
                                </h4>
                                <div className="space-y-1 text-sm">
                                  <p><span className="text-muted-foreground">Total Annual Cost:</span> {formatCurrency((submission as CompanySubmission).totalAnnualCost || 0)}</p>
                                  <p><span className="text-muted-foreground">Confidence:</span> {(submission as CompanySubmission).quantification?.confidenceLevel || 'Not specified'}</p>
                                </div>
                              </div>
                              {(submission as CompanySubmission).rootCause && (
                                <div className="col-span-2">
                                  <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                    <Target className="h-4 w-4" />
                                    Root Cause
                                  </h4>
                                  <p className="text-sm">{(submission as CompanySubmission).rootCause}</p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {submission.type === 'public' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                  <MapPin className="h-4 w-4" />
                                  Location
                                </h4>
                                <p className="text-sm">{(submission as PublicSubmission).location || 'Not specified'}</p>
                              </div>
                              <div>
                                <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                  <Tag className="h-4 w-4" />
                                  Categories
                                </h4>
                                <p className="text-sm">{(submission as PublicSubmission).category?.join(', ') || 'Not specified'}</p>
                              </div>
                              {(submission as PublicSubmission).organization && (
                                <div>
                                  <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                    <Building2 className="h-4 w-4" />
                                    Organization
                                  </h4>
                                  <p className="text-sm">{(submission as PublicSubmission).organization}</p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {submission.documents && submission.documents.length > 0 && (
                            <div>
                              <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                <FileText className="h-4 w-4" />
                                Documents
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {submission.documents.map((doc, i) => (
                                  <a 
                                    key={i} 
                                    href={doc} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                  >
                                    <FileText className="h-3 w-3" />
                                    Document {i + 1}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {submission.reviewNotes && (
                            <div>
                              <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                <Info className="h-4 w-4" />
                                Review Notes
                              </h4>
                              <p className="text-sm p-2 bg-muted rounded-md whitespace-pre-wrap">
                                {submission.reviewNotes}
                              </p>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Assignment Dialog */}
      <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Assign Submission to Problem Bank Analyst</DialogTitle>
            <DialogDescription>
              Select a Problem Bank Analyst to review and convert this submission. 
              The status will automatically change to "Under Review".
            </DialogDescription>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="font-medium">{selectedSubmission.problemTitle}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Submitted by: {selectedSubmission.type === 'company' 
                    ? (selectedSubmission as CompanySubmission).companyName 
                    : (selectedSubmission as PublicSubmission).fullName}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Current status: <Badge variant="outline" className="text-xs">{selectedSubmission.status}</Badge>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Select Problem Bank Analyst <span className="text-red-500">*</span></Label>
                <Select value={selectedAnalystId} onValueChange={setSelectedAnalystId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an analyst..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAnalysts.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No Problem Bank Analysts available
                      </div>
                    ) : (
                      availableAnalysts.map(analyst => (
                        <SelectItem key={analyst.id} value={analyst.id}>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {getInitials(analyst.name)}
                              </AvatarFallback>
                            </Avatar>
                            <span>{analyst.name}</span>
                            <Badge variant="outline" className="ml-2 text-xs">
                              Problem Bank
                            </Badge>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {availableAnalysts.length} Problem Bank Analyst{availableAnalysts.length !== 1 ? 's' : ''} available
                </p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> After assignment, the submission status will change to "Under Review" 
                  and the analyst will be able to review and convert it.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignmentDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignSubmission} 
              disabled={!selectedAnalystId || isAssigning}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isAssigning ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</>
              ) : (
                <><UserCheck className="mr-2 h-4 w-4" />Assign & Mark Under Review</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}